import { useMutation } from '@tanstack/react-query';

import { useInvalidateUserData, useUserPlan, useUser } from '@/states/auth';
import {
  useSignInModal,
  useUpgradeSubscriptionModal,
  useCancelSubscriptionModal,
} from '@/providers/modal/states';
import { client } from '@/client';

export const useSubscription = () => {
  const { planData } = useUserPlan();
  const user = useUser();
  const signInModal = useSignInModal();
  const cancelModal = useCancelSubscriptionModal();
  const upgradeModal = useUpgradeSubscriptionModal();

  const invalidateUserInfo = useInvalidateUserData();

  const toggleSubscription = useMutation({
    mutationFn: () => client.post('/purchase/toggle-subscription'),
    onSuccess: invalidateUserInfo,
  });

  const cancelSubscription = useMutation({
    mutationFn: () => client.post('/purchase/cancel-subscription'),
    onSuccess: () => {
      invalidateUserInfo();
      cancelModal.close();
    },
  });

  const resumeSubscription = useMutation({
    mutationFn: () => client.post('/purchase/resume-subscription'),
    onSuccess: invalidateUserInfo,
  });

  const upgradeSubscription = useMutation({
    mutationFn: () => client.post('/purchase/upgrade-subscription'),
    onSuccess: () => {
      invalidateUserInfo();
      upgradeModal.close();
    },
  });

  const downgradeSubscription = useMutation({
    mutationFn: () => client.post('/purchase/downgrade-subscription'),
    onSuccess: invalidateUserInfo,
  });

  const handleCancel = () => {
    if (!user?.email) {
      signInModal.open();
      return;
    }
    cancelModal.open();
  };

  const handleResume = () => {
    if (!user?.email) {
      signInModal.open();
      return;
    }
    resumeSubscription.mutate();
  };

  const handleUpgrade = () => {
    if (!user?.email) {
      signInModal.open();
      return;
    }
    upgradeModal.open();
  };

  const handleDowngrade = () => {
    if (!user?.email) {
      signInModal.open();
      return;
    }
    downgradeSubscription.mutate();
  };

  return {
    planData,
    isLoading:
      toggleSubscription.isPending ||
      cancelSubscription.isPending ||
      resumeSubscription.isPending ||
      upgradeSubscription.isPending ||
      downgradeSubscription.isPending,
    handleCancel,
    handleResume,
    handleUpgrade,
    handleDowngrade,
    // Expose mutations for modals
    cancelSubscription,
    upgradeSubscription,
    downgradeSubscription,
    resumeSubscription,
  };
};
